import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Church, Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF9F5] p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-xl bg-[#2D5A27] flex items-center justify-center text-white">
            <Church className="h-6 w-6" />
          </div>
          <span className="text-2xl font-serif font-semibold">FlockCare</span>
        </div>
        {sent ? (
          <div className="rounded-2xl bg-white border border-[#E5E0D8] p-8 shadow-sm text-center" data-testid="forgot-sent">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#EAF2E8] flex items-center justify-center">
              <MailCheck className="h-6 w-6 text-[#2D5A27]" />
            </div>
            <h2 className="mt-4 font-serif text-2xl font-bold">Check your inbox</h2>
            <p className="mt-2 text-sm text-[#7A8A78]">
              If that email is registered, a reset link has been sent. It expires in one hour.
            </p>
            <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#2D5A27] hover:underline">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-serif text-3xl font-bold">Reset your password</h2>
            <p className="mt-2 text-sm text-[#7A8A78]">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input id="email" type="email" data-testid="forgot-email-input" value={email}
                  onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" required />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="forgot-submit-button"
                className="h-11 w-full bg-[#2D5A27] hover:bg-[#23471F] text-white font-semibold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </Button>
              <Link to="/login" className="flex items-center justify-center gap-2 text-sm font-medium text-[#2D5A27] hover:underline">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
